import { useAsyncExecutor, useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import moment from "moment-timezone";
import { Checkbox } from "primereact/checkbox";
import { Paginator } from "primereact/paginator";
import { parse, stringify } from "query-string";
import { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  combineLatest,
  filter,
  from,
  interval,
  map,
  mapTo,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";

import {
  Authorization,
  useAuthorization,
} from "../../../common/AuthorizationCpt";
import { DivCheckbox } from "../../../common/components/PaymentQuotedTableStyles";
import { getErrorValueResponse } from "../../../common/error/Error";
import { FILTERS, HTTP_METHODS } from "../../../utils/constants";
import { dateToRegularFormat } from "../../../utils/dates";
import { networkBackendRequest } from "../../../utils/networkUtils";
import { formatMoney } from "../../../utils/numbers";
import { USER_ROUTES } from "../../../utils/routeConstants";
import { InfoSelectPlanel } from "../../AdminPaymentValidationStatesSidebar/modals/infoSelectPanel";
import CommentModal from "../../CommentModal/CommentModal";
import ConfirmationModal from "../../ConfirmationModal/ConfirmationModal";
import { CurrencyContext } from "../../CurrencyContext/CurrencyContext";
import {
  ColoredTag,
  InvoiceContainer,
  StatusDiv,
  VolanteId,
} from "../../Invoice/InvoiceStyles";
import { InvoicesContainer, TableHeader } from "../../Invoices/InvoicesStyles";
import PaymentLoader from "../../PaymentLoader/PaymentLoader";
import PaymentModal from "../../PaymentModal/PaymentModal";
import StandaloneMessage from "../../StandaloneMessage/StandaloneMessage";
import { paymentConvertResponse, POLLING_INTERVAL } from "../common";
import {
  getPaymentTableHeaders,
  ITEMS_PER_PAGE,
  PAYMENT_TABLE_MODAL_STATES,
} from "../common/utils";
import { ButtonContainer, Container, MainButton } from "../PaymentStyles";

export const PaymentPendingTable = (props) => {
  const { params$, shoppingCar } = props;

  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;

  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  const [dateUrlQuotationPending, setDateUrlQuotationPending] = useState(
    new Date()
  );
  const loadInvoices = useAsyncExecutor2(
    (params) => {
      const destroy$ = destroy$Ref.current;
      return interval(POLLING_INTERVAL).pipe(
        takeUntil(destroy$),
        startWith(params),
        mapTo(params),
        switchMap((params: any) => {
          setDateUrlQuotationPending(params.from_date);
          const request = networkBackendRequest(
            "/quotations/pending/",
            HTTP_METHODS.GET,
            params,
            undefined
          )
            .then((response) => response.json())
            .then(paymentConvertResponse);
          return from(request);
        })
      );
    },
    {
      params$: params$.pipe(
        takeUntil(destroy$Ref.current),
        map((params: any) => ({
          ...params,
          from_date: format(params.from_date, "yyyy-MM-dd"),
          to_date: format(params.to_date, "yyyy-MM-dd"),
          order: "descending",
          page: params.page ?? 1,
          page_size: ITEMS_PER_PAGE,
          payment_type_list: (params.payment_type_list.length > 0
            ? params.payment_type_list
            : FILTERS.PAYMENT.CONDITION.map((option) => option.value)
          ).map((val) => val.toLowerCase()),
          payment_status_list: (params.payment_status_list.length > 0
            ? params.payment_status_list
            : FILTERS.PAYMENT.STATUS.map((option) => option.value)
          ).map((val) => val.toLowerCase()),
        }))
      ),
    }
  );

  const [invoicesStatus, setInvoicesStatus] = useState<any>({
    status: "",
    data: { details: [], num_page: 0, page_size: 0, params: {} },
  });

  useEffect(() => {
    const checkedInvoices$ = combineLatest(
      loadInvoices.state$,
      shoppingCar.items.valueChanges.pipe(startWith(shoppingCar.items.value)),
      (invoicesStatus, addedItems: any) => {
        if (invoicesStatus.status !== "SUCCESS") return invoicesStatus;
        const invoices = invoicesStatus.data;
        const details = invoices.details.map((invoice) => {
          const checked = addedItems.some((x) => {
            return x.id === invoice.id;
          });
          return { checked, invoice };
        });
        return { ...invoicesStatus, data: { ...invoices, details } };
      }
    );

    const checkedAll$ = checkedInvoices$.pipe(
      filter((rowsStatus) => rowsStatus.status === "SUCCESS"),
      map((rowsStatus) => rowsStatus.data.details),
      map((rows) => {
        return rows.filter((row) => !row.invoice.disabledPending);
      }),
      map((rows) => {
        return rows.every((row) => row.checked);
      }),
      tap((checkedAll) => {
        setCheckedAll(checkedAll);
      })
    );

    const checkedAllSubscription = checkedAll$.subscribe();

    const checkedInvoicesSubscription = checkedInvoices$
      .pipe(
        tap((invoicesStatus) => {
          setInvoicesStatus({
            status: invoicesStatus.status,
            data: invoicesStatus.data,
            error: invoicesStatus.error,
            params: invoicesStatus.params,
          });
        })
      )
      .subscribe();

    return () => {
      checkedInvoicesSubscription.unsubscribe();
      checkedAllSubscription.unsubscribe();
    };
  }, [loadInvoices.state$, shoppingCar]);

  const [checkedAll, setCheckedAll] = useState(false);
  const [useDollars] = useContext(CurrencyContext);
  const titles = getPaymentTableHeaders(useDollars);
  const [showModal, updateShowModal] = useState(false);

  const hideModalPaymentType = () => {
    updateShowModal(false);
  };

  const [messageError1, updateMessageError1] = useState("");
  const [showModalError1, setShowModalError1] = useState(false);
  const [messageSuccessCreditMethod, setMessageSuccessCreditMethod] =
    useState("");

  const [modalSuccessCreditMethod, setModalSuccessCreditMethod] =
    useState(false);

  const history = useHistory();

  const gridTemplateColumns = "4rem 1fr repeat(3, 15rem) 1fr 15rem";

  const [showRequoteModal, updateShowRequoteModal] = useState(false);
  const [countNextPagePending, setCountNextPagePending] = useState(0);

  const patchQuotation = useAsyncExecutor2(
    (params: any) => {
      const { quotation_ids } = params;
      return networkBackendRequest("/quotations/", HTTP_METHODS.PATCH, null, {
        quotation_ids,
      });
    },
    {
      onSuccess: () => {
        getQuotationPendingMulti.execute({
          from_date: dateUrlQuotationPending,
        });
      },
      onError: (error) => {
        setCountNextPagePending(1);
        const errorMessage = getErrorValueResponse(error);
        setMessageSuccessPaymentDeclined(errorMessage);
        setTitleSuccessPaymentDeclined("Atención");
        setShowModalPaymentDeclined(true);
        shoppingCar.items.clear();
        updateShowRequoteModal(false);
      },
    }
  );
  const getQuotationPendingMulti: any = useAsyncExecutor2(
    (params: any) => {
      const from_date = format(params.from_date, "yyyy-MM-dd");
      return networkBackendRequest(`/quotations/pending/`, HTTP_METHODS.GET, {
        from_date,
      }).then((resp) => resp.json());
    },
    {
      onSuccess: (data) => {
        const count = data.count;
        setCountNextPagePending(count);
        setMessageSuccessPaymentDeclined(
          "Se volvió a generar las pre-facturas exitosamente."
        );
        setTitleSuccessPaymentDeclined(
          "Restauración desarrollada exitosamente."
        );
        setShowModalPaymentDeclined(true);
        updateShowRequoteModal(false);
      },
      onError: () => {
        setCountNextPagePending(1);
        setMessageSuccessPaymentDeclined(
          "Se volvió a generar las pre-facturas exitosamente."
        );
        setTitleSuccessPaymentDeclined(
          "Restauración desarrollada exitosamente."
        );
        setShowModalPaymentDeclined(true);
        updateShowRequoteModal(false);
      },
    }
  );

  const onCloseModal = (refreshNeeded) => {
    if (refreshNeeded === true) {
      shoppingCar.items.clear();
      loadInvoices.retry();
    }
  };
  const [showModalPaymentDeclined, setShowModalPaymentDeclined] =
    useState(false);
  const [messageSuccessPaymentDeclined, setMessageSuccessPaymentDeclined] =
    useState("");
  const [titleSuccessPaymentDeclined, setTitleSuccessPaymentDeclined] =
    useState("");

  const [showErrorLengthDiferent, setShowErrorLengthDiferent] = useState("");

  const getQuotationPendingVolantesTable: any = useAsyncExecutor2(
    (params: any) => {
      const { invoices } = params;
      const volante_nums = invoices.map((volante) => volante.volanteId);
      return networkBackendRequest(`/quotations/pending/`, HTTP_METHODS.GET, {
        volante_nums,
        page_size: volante_nums.length,
      })
        .then((resp) => resp.json())
        .then(paymentConvertResponse);
    },
    {
      onSuccess: (data, params) => {
        const { quotation_ids } = data;
        const { invoices } = params;
        if (invoices.length !== quotation_ids.length) {
          setShowErrorLengthDiferent(
            "Alguno de los volantes ya fue restaurado. Agradeceremos contactarse con el equipo de Soporte Talmaclick."
          );
          updateShowRequoteModal(false);
        }
      },
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);
        setShowErrorLengthDiferent(errorMessage);
        updateShowRequoteModal(false);
      },
    }
  );

  return (
    <>
      {showErrorLengthDiferent && (
        <ConfirmationModal
          message={showErrorLengthDiferent}
          onAccept={() => {
            setShowErrorLengthDiferent("");
            shoppingCar.items.clear();
          }}
          title={`Atención`}
          toggleModal={() => {
            setShowErrorLengthDiferent("");
            shoppingCar.items.clear();
          }}
        />
      )}
      {showModalPaymentDeclined && messageSuccessPaymentDeclined && (
        <ConfirmationModal
          message={messageSuccessPaymentDeclined}
          onAccept={() => {
            if (titleSuccessPaymentDeclined !== "Atención") {
              shoppingCar.items.clear();
            }
            setMessageSuccessPaymentDeclined("");
            setTitleSuccessPaymentDeclined("");
            setShowModalPaymentDeclined(false);
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            if (countNextPagePending > 0) {
              history.push(`${pathname}?${stringifiedParams}`);
            } else {
              history.push(USER_ROUTES.PAYMENT_PRE_BILLS.path);
            }
          }}
          title={titleSuccessPaymentDeclined}
          toggleModal={() => {
            if (titleSuccessPaymentDeclined !== "Atención") {
              shoppingCar.items.clear();
            }
            setMessageSuccessPaymentDeclined("");
            setTitleSuccessPaymentDeclined("");
            setShowModalPaymentDeclined(false);
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            if (countNextPagePending > 0) {
              history.push(`${pathname}?${stringifiedParams}`);
            } else {
              history.push(USER_ROUTES.PAYMENT_PRE_BILLS.path);
            }
          }}
        />
      )}
      <InvoicesContainer gridTemplateColumns={gridTemplateColumns}>
        {invoicesStatus.status === "PROCESSING" && (
          <>
            <PaymentLoader numItems={ITEMS_PER_PAGE} />
          </>
        )}
        {invoicesStatus.status === "SUCCESS" && (
          <>
            {invoicesStatus.data.details.length === 0 && (
              <StandaloneMessage>
                No hay ningún volante pendiente de validación.
              </StandaloneMessage>
            )}

            {invoicesStatus.data.details.length > 0 && (
              <>
                <TableHeader className="item-container">
                  <div>
                    <input
                      className="checkInvoice clickable-element"
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          invoicesStatus.data.details
                            .filter((item) => !item.invoice.disabledPending)
                            .forEach((item) => {
                              const { invoice } = item;
                              shoppingCar.addItem(invoice);
                            });
                        } else {
                          invoicesStatus.data.details
                            .filter((item) => !item.invoice.disabledPending)
                            .forEach((item) => {
                              const { invoice } = item;
                              shoppingCar.removeItem(invoice.id);
                            });
                        }
                      }}
                      checked={
                        !invoicesStatus.data?.disabledAllPending && checkedAll
                      }
                      disabled={invoicesStatus.data?.disabledAllPending}
                      style={{
                        cursor: `${
                          invoicesStatus.data?.disabledAllPending
                            ? "not-allowed"
                            : "pointer"
                        }`,
                      }}
                    />
                  </div>
                  <div className="main-info-header">{titles.importer}</div>
                  <div>{titles.paymentType}</div>
                  <div>{titles.status}</div>
                  <div>{titles.retirement}</div>
                  <div className="main-info-header">{titles.rucId}</div>
                  <div>{titles.amount}</div>
                </TableHeader>
                {invoicesStatus.data.details.map((item) => {
                  const { checked, invoice } = item;
                  const {
                    consigneeRuc,
                    importer,
                    volanteId,
                    withdrawalDate,
                    ruc,
                    amount,
                    disabledPending,
                  } = invoice;
                  const today = moment(
                    new Date(new Date().setHours(0, 0, 0, 0))
                  );
                  return (
                    <div key={invoice.id}>
                      <InvoiceContainer className="item-container">
                        <div className="attribute">
                          <div className="titles">{titles.select}</div>
                          <DivCheckbox disabled={disabledPending}>
                            <Checkbox
                              className="checkInvoice clickable-element checkbox-react-prime-class"
                              checked={checked}
                              onChange={(e) => {
                                if (e.checked) {
                                  shoppingCar.addItem(invoice);
                                } else {
                                  shoppingCar.removeItem(invoice.id);
                                }
                              }}
                              disabled={disabledPending}
                            />
                          </DivCheckbox>
                        </div>
                        <div className="attribute main-info-header">
                          <div className="titles">{titles.importer}</div>
                          <div className="main-info-header">
                            <p className="id">{consigneeRuc}</p>
                            <p>{importer}</p>
                            <VolanteId>{volanteId}</VolanteId>
                          </div>
                        </div>
                        <PaymentTableColumnType
                          paymentType={titles.paymentType}
                          invoice={invoice}
                        />
                        <PaymentTableColumnStatus
                          status={titles.status}
                          invoice={invoice}
                          refreshPage={() => {
                            loadInvoices.retry();
                          }}
                          onSelect={(invoice) => {
                            shoppingCar.addItem(invoice);
                          }}
                          setShowModalPaymentDeclined={
                            setShowModalPaymentDeclined
                          }
                          setMessageSuccessPaymentDeclined={
                            setMessageSuccessPaymentDeclined
                          }
                          setTitleSuccessPaymentDeclined={
                            setTitleSuccessPaymentDeclined
                          }
                          countPageRedirect={(data) => {
                            setCountNextPagePending(data);
                          }}
                          dateUrlQuotationPending={dateUrlQuotationPending}
                          setShowErrorLengthDiferent={
                            setShowErrorLengthDiferent
                          }
                        />
                        <div className="attribute">
                          <div className="titles">{titles.retirement}</div>
                          <ColoredTag
                            hasExpired={moment(withdrawalDate) < today}
                          >
                            {dateToRegularFormat(withdrawalDate)}
                          </ColoredTag>
                        </div>

                        <div className="attribute main-info-header">
                          <div className="titles">{titles.rucId}</div>
                          <div className="main-info-header">
                            <p className="ruc-id">{ruc.ruc}</p>
                            <p>{ruc.name}</p>
                          </div>
                        </div>
                        <div className="attribute">
                          <div className="titles">{titles.amount}</div>
                          <div>{formatMoney(amount, useDollars)}</div>
                        </div>
                      </InvoiceContainer>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </InvoicesContainer>

      {invoicesStatus.status === "SUCCESS" && (
        <>
          {invoicesStatus.data.count > 0 && (
            <Container>
              <div className="flex-paginator">
                <div className="flex-paginator">
                  <Paginator
                    first={
                      (invoicesStatus.params.page - 1) *
                      invoicesStatus.data.page_size
                    }
                    rows={invoicesStatus.data.page_size}
                    totalRecords={invoicesStatus.data.count}
                    onPageChange={(e) => {
                      const targetPage =
                        e.first / invoicesStatus.data.page_size;
                      const pathname = history.location.pathname;
                      const parsedParams = parse(history.location.search);
                      const stringifiedParams = stringify({
                        ...parsedParams,
                        page: targetPage + 1,
                      });
                      history.push(`${pathname}?${stringifiedParams}`);
                    }}
                  />
                </div>
              </div>
              <div>
                <ButtonContainer>
                  <Authorization
                    actions={["quotation.update_quotation_rollback"]}
                  >
                    <MainButton
                      disabled={
                        shoppingCar.items.value.length === 0 ||
                        loadInvoices.isLoading ||
                        showModal
                      }
                      onClick={() => {
                        updateShowRequoteModal(true);
                        getQuotationPendingVolantesTable.execute({
                          invoices: shoppingCar.items.value,
                        });
                      }}
                    >
                      {"Volver a generar pre-facturas ("}
                      {shoppingCar.items.value.length}
                      {")"}
                    </MainButton>
                  </Authorization>
                </ButtonContainer>
              </div>
            </Container>
          )}
        </>
      )}
      {showRequoteModal && (
        <CommentModal
          waitingForServerResponse={
            patchQuotation.isLoading || getQuotationPendingMulti.isLoading
          }
          toggleModal={() => {
            updateShowRequoteModal(false);
          }}
          title="Restaurar pre-facturas"
          message={`Está a punto de restaurar ${
            shoppingCar.items.value.length
          } pre-factura${
            shoppingCar.items.value.length === 1 ? "" : "s"
          }. Asegúrese de leer los comentarios antes de realizar esta acción. ¿Está seguro que desea realizarla?`}
          buttonText="Confirmar restauración"
          onAccept={() => {
            patchQuotation.execute({
              quotation_ids: shoppingCar.items.value.map(
                (quotation) => quotation.id
              ),
            });
          }}
          disabled={!getQuotationPendingVolantesTable.isSuccess}
        />
      )}
      {showModal && (
        <PaymentModal
          toggleModal={(refreshNeeded) => {
            if (refreshNeeded) {
              loadInvoices.retry();
            }
            updateShowModal(false);
          }}
          invoices={shoppingCar.items.value}
          hideModalPaymentType={hideModalPaymentType}
          hideModalPaymentTypeShowModal={showModal}
          updateMessageError1={updateMessageError1}
          setShowModalError1={setShowModalError1}
          setMessageSuccessCreditMethod={setMessageSuccessCreditMethod}
          setModalSuccessCreditMethod={setModalSuccessCreditMethod}
        />
      )}

      {showModalError1 && messageError1 && (
        <ConfirmationModal
          message={messageError1}
          onAccept={() => {
            setShowModalError1(false);
            onCloseModal(true);
          }}
          title={`Atención`}
          toggleModal={() => {
            setShowModalError1(false);
            onCloseModal(true);
          }}
        />
      )}
      {modalSuccessCreditMethod && messageSuccessCreditMethod && (
        <ConfirmationModal
          title={"Facturación al crédito confirmada"}
          toggleModal={() => {
            setModalSuccessCreditMethod(false);
            onCloseModal(true);
          }}
          message={messageSuccessCreditMethod}
          onAccept={() => {
            setModalSuccessCreditMethod(false);
            onCloseModal(true);
          }}
        />
      )}
    </>
  );
};

const PaymentTableColumnStatus = (props) => {
  const {
    status,
    invoice,
    refreshPage,
    onSelect,
    setShowModalPaymentDeclined,
    setMessageSuccessPaymentDeclined,
    setTitleSuccessPaymentDeclined,
    countPageRedirect,
    dateUrlQuotationPending,
    setShowErrorLengthDiferent,
  } = props;
  const { paymentStatus, quotationRejectedComment, id, disabledPending } =
    invoice;
  const authorization = useAuthorization();

  const isAllowedToEdit = authorization.isAllowedByActions(
    "quotation.update_quotation_rollback"
  );

  const [modalState, updatetModalState] = useState(
    PAYMENT_TABLE_MODAL_STATES.disabled
  );

  const toggleCommentModal: any = (refreshNeeded) => {
    if (refreshNeeded) {
      refreshPage();
    }
    updatetModalState(
      modalState === PAYMENT_TABLE_MODAL_STATES.disabled
        ? PAYMENT_TABLE_MODAL_STATES.comment
        : PAYMENT_TABLE_MODAL_STATES.disabled
    );
  };

  const [quotation_ids, setQuotation_ids] = useState<[]>([]);

  const getQuotationPendingVolantes: any = useAsyncExecutor2(
    (params: any) => {
      const { invoice } = params;
      const volante_nums = invoice.volanteId;
      return networkBackendRequest(`/quotations/pending/`, HTTP_METHODS.GET, {
        volante_nums: [volante_nums],
      })
        .then((resp) => resp.json())
        .then(paymentConvertResponse);
    },
    {
      onSuccess: (data) => {
        const { quotation_ids } = data;
        setQuotation_ids(quotation_ids);
      },
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);
        setShowErrorLengthDiferent(errorMessage);
      },
    }
  );

  const onClickStatus = () => {
    if (disabledPending) return;
    if (!isAllowedToEdit) return;
    getQuotationPendingVolantes.execute({ invoice, id });
    onSelect(invoice);
    toggleCommentModal();
  };

  const saveComments = useAsyncExecutor(
    (params: any) => {
      const { id } = params;
      return networkBackendRequest("/quotations/", HTTP_METHODS.PATCH, null, {
        quotation_ids: [id],
      });
    },
    {
      onSuccess: () => {
        getQuotationPending.execute({ from_date: dateUrlQuotationPending });
      },
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);
        setMessageSuccessPaymentDeclined(errorMessage);
        countPageRedirect(1);
        setTitleSuccessPaymentDeclined("Atención");
        setShowModalPaymentDeclined(true);
      },
    }
  );

  const getQuotationPending: any = useAsyncExecutor2(
    (params: any) => {
      return networkBackendRequest(`/quotations/pending/`, HTTP_METHODS.GET, {
        from_date: params.from_date,
      }).then((resp) => resp.json());
    },
    {
      onSuccess: (data) => {
        const count = data.count;
        countPageRedirect(count);
        setMessageSuccessPaymentDeclined(
          "Se volvió a generar la pre-factura exitosamente."
        );
        setTitleSuccessPaymentDeclined("Confirmado con éxito.");
        setShowModalPaymentDeclined(true);
      },
      onError: () => {
        countPageRedirect(1);
        setMessageSuccessPaymentDeclined(
          "Se volvió a generar la pre-factura exitosamente."
        );
        setTitleSuccessPaymentDeclined("Confirmado con éxito.");
        setShowModalPaymentDeclined(true);
      },
    }
  );

  return (
    <>
      <div className="attribute">
        <div className="titles">{status}</div>
        <StatusDiv status={paymentStatus.category} onClick={onClickStatus}>
          <span
            style={{
              cursor: `${
                paymentStatus.category === "warning" ? "default" : "pointer"
              }`,
            }}
          >
            {paymentStatus.label.toUpperCase()}
          </span>
        </StatusDiv>
      </div>

      {modalState === PAYMENT_TABLE_MODAL_STATES.comment && (
        <CommentModal
          waitingForServerResponse={
            saveComments.isLoading || getQuotationPending.isLoading
          }
          toggleModal={toggleCommentModal}
          title={invoice.paymentStatus.label}
          message={quotationRejectedComment}
          buttonText="Volver a generar pre-factura"
          onAccept={() => {
            saveComments.execute({ id });
          }}
          disabled={
            quotation_ids.length < 0 || getQuotationPendingVolantes.isLoading
          }
        />
      )}
    </>
  );
};

const PaymentTableColumnType = (props) => {
  const { paymentType, invoice } = props;
  const {
    typePaymentCreditContaReca,
    payerRucOption,
    bankPaymentCode,
    payment_type,
    bankTypeLabel,
  } = invoice;
  return (
    <div className="attribute">
      <div className="titles">{paymentType}</div>
      <div>
        {invoice.typePaymentCreditContaReca === "upfront_manual" ? (
          <>
            <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
              {payment_type.label}
            </div>
            <InfoSelectPlanel
              fontSizeCounLabel={"1.4rem"}
              iconCountWith={"2.5rem"}
              iconCountHeight={"2.5rem"}
              items={invoice.vouchers.map((voucher) => voucher.bank_name.name)}
              height={"3.2rem"}
              widthPanel={"28.1rem"}
              bankType="table-bank"
            ></InfoSelectPlanel>
          </>
        ) : (
          <>
            <div style={{ textAlign: "center" }}>{payment_type.label}</div>

            {typePaymentCreditContaReca === "credit" && (
              <VolanteId style={{ textAlign: "center" }}>
                {payerRucOption === "consignee"
                  ? "Agente importador"
                  : payerRucOption === "third_party_agency"
                  ? "Crédito de Terceros"
                  : "Agente de carga"}
              </VolanteId>
            )}

            <VolanteId
              style={{ fontSize: "1.4rem", color: "#08082B", marginTop: 6 }}
            >
              {bankPaymentCode}
            </VolanteId>
            {bankTypeLabel && (
              <div
                className="id"
                style={{ marginTop: "0.4rem", textAlign: "center" }}
              >
                ({bankTypeLabel})
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
